import { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Text } from '@servicetitan/anvil2';

import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { Breadcrumbs } from '../../common/components/breadcrumbs/breadcrumbs';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { EquipmentPickerStore } from '../store/equipment-picker.store';
import { MessageRow } from '../../common/components/message-row/message-row';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { EquipmentOptions } from './equipment-options';
import { SearchEquipment } from './search-equipment';
import { InstalledEquipmentApiModel } from '../../common/api/equipments.api';
import { MessageType } from '../../common/models/message-type.model';
import { ErrorView } from '../../common/components/error-view';
import { ErrorModel } from '../../common/models/error.model';
import { Loader } from '../../common/components/loader/loader';
import { ManualEquipmentData } from '../store/equipment-manual-select.store';
import { EquipmentManualSelect } from './equipment-manual-select';
import { MainStore } from '../../common/stores/main.store';

export const EquipmentPickerPage = provide({
    singletons: [],
})(
    observer(() => {
        const [equipmentStore, chatStore, mainStore] = useDependencies<
            [EquipmentPickerStore, ChatStore, MainStore]
        >(EquipmentPickerStore, ChatStore, MainStore);

        const history = useHistory();

        const handleBack = () => {
            history.goBack();
        };

        useEffect(() => {
            let stillHere = true;

            async function init() {
                const succeed = await equipmentStore.proceedFrom();
                equipmentStore.showSelectEquipment();
                if (stillHere && !succeed) {
                    history.push(`/${mainStore.selectedAppointment?.id}/topic-picker`);
                }
            }

            init();

            return () => {
                stillHere = false;
                equipmentStore.dispose();
            };
        }, [equipmentStore, history, mainStore.selectedAppointment?.id]);

        const handleEquipmentClick = useCallback(
            async (equipment: InstalledEquipmentApiModel) => {
                const mappedEquipments = await equipmentStore.setSelectedEquipment(equipment);
                const mappedIds = mappedEquipments?.map(me => me.id);
                if (mappedIds && mappedIds.length > 0) {
                    setTimeout(() => {
                        history.push(
                            `/${mainStore.selectedAppointment?.id}/${mainStore.selectedTopic?.id}/${mappedIds.join(',')}/questions`
                        );
                    }, 1000);
                } else {
                    setTimeout(() => {
                        history.push(
                            `/${mainStore.selectedAppointment?.id}/${mainStore.selectedTopic?.id}/${mainStore.selectedMonolithEquipment?.equipment_id}/questions`
                        );
                    }, 1000);
                }
            },
            [
                mainStore.selectedMonolithEquipment?.equipment_id,
                mainStore.selectedAppointment?.id,
                mainStore.selectedTopic?.id,
                equipmentStore,
                history,
            ]
        );

        const handleManualEquipmentSubmit = useCallback(
            async (data: ManualEquipmentData | undefined) => {
                if (!data) {
                    return;
                }
                const mappedEquipments = await equipmentStore.submitManualEquipment(data);
                const mappedIds = mappedEquipments?.map(me => me.id);
                if (mappedIds) {
                    history.push(
                        `/${mainStore.selectedAppointment?.id}/${mainStore.selectedTopic?.id}/${mappedIds.join(',')}/questions`
                    );
                }
            },
            [
                mainStore.selectedAppointment?.id,
                mainStore.selectedTopic?.id,
                equipmentStore,
                history,
            ]
        );

        let myMessage = 0;
        let botMessage = 0;

        const renderMessage = useCallback(
            (message: IMessage, key: string, showAvatar: boolean, tenantName = 'tenant name') => {
                switch (message.type) {
                    case MessageType.Text:
                        return (
                            <MessageRow
                                key={key}
                                isMine={message.isMine}
                                showAvatar={showAvatar}
                                name={tenantName}
                                date={message.date}
                            >
                                <MessageBubble
                                    isMine={message.isMine}
                                    showBubble={message.showBubble}
                                >
                                    <Text>{message.text}</Text>
                                </MessageBubble>
                            </MessageRow>
                        );

                    case MessageType.RichText:
                        return (
                            <MessageRow
                                key={key}
                                isMine={message.isMine}
                                showAvatar={showAvatar}
                                name={tenantName}
                                date={message.date}
                            >
                                <MessageBubble isMine={message.isMine}>
                                    <Text>
                                        {/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */}
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: message.text ?? '',
                                            }}
                                        />
                                        {/* eslint-enable react/no-danger,@typescript-eslint/naming-convention */}
                                    </Text>
                                </MessageBubble>
                            </MessageRow>
                        );

                    case MessageType.EquipmentOptions:
                        return (
                            <MessageRow
                                key={key}
                                date={message.date}
                                isMine={message.isMine}
                                showAvatar={false}
                            >
                                <EquipmentOptions
                                    selectedEquipment={equipmentStore.selectedEquipment}
                                    equipments={message.data as InstalledEquipmentApiModel[]}
                                    onSelect={handleEquipmentClick}
                                />
                            </MessageRow>
                        );

                    case MessageType.SearchEquipment:
                        return (
                            <MessageRow key={key} isMine={message.isMine} date={message.date}>
                                <SearchEquipment onSubmit={handleEquipmentClick} />
                            </MessageRow>
                        );

                    case MessageType.ManualEquipment:
                        return (
                            <MessageRow key={key} isMine={message.isMine} date={message.date}>
                                <EquipmentManualSelect
                                    isLoading={equipmentStore.isFetchingInfo}
                                    onSubmit={handleManualEquipmentSubmit}
                                />
                            </MessageRow>
                        );

                    case MessageType.Error:
                        return (
                            <MessageRow key={key} isMine={message.isMine} date={message.date}>
                                <ErrorView data={message.data as ErrorModel} />
                            </MessageRow>
                        );
                    default:
                        return null;
                }
            },
            [
                equipmentStore.isFetchingInfo,
                equipmentStore.selectedEquipment,
                handleEquipmentClick,
                handleManualEquipmentSubmit,
            ]
        );

        return (
            <ChatLayout
                subHeader={<Breadcrumbs />}
                header={<ChatHeaderNew onNavigationButtonClick={handleBack} icon="back" />}
            >
                {chatStore.messages.map((message, index) => {
                    if (message.isMine) {
                        myMessage++;
                        botMessage = 0;
                    } else {
                        botMessage++;
                        myMessage = 0;
                    }
                    return renderMessage(
                        message,
                        index.toString(),
                        myMessage === 1 || botMessage === 1
                    );
                })}
                {equipmentStore.loading && (
                    <MessageRow isMine={false}>
                        <Loader />
                    </MessageRow>
                )}
            </ChatLayout>
        );
    })
);
